// src/components/mobile/components/MobileRosterPlanModal.js
//
// Mobile drop-shuffle plan modal. Per ROSTER_PLAN_SPEC.md § B.5.7.
//
// Render-only component — all state is owned by useRosterPlan
// (shared with desktop). Mirrors RosterPlanModal.js architecture
// adapted for mobile UX patterns:
//   - Centered card (NOT bottom sheet), max-width 324px
//   - Backdrop tap dismissal
//   - Inline-style sizing matching existing mobile UI conventions
//     (padding 9px, fontSize 9–11px, icons 12–15px)
//   - dynastyTheme classes for colors only (no theme button helpers
//     on mobile — mobile composes atomic bg/text classes per
//     MobilePositionAssignmentModal.js precedent)
//
// State machine: same as desktop (idle → select_drop →
// loading_plans → choose_plan → confirming → done | error |
// cancelled). Hook owns transitions; modal is render-only.

import React from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  Check,
  Loader2,
  X,
} from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';

const C = dynastyTheme.tokens.colors;

const HUMAN_INFEASIBLE = {
  infeasible_no_shuffle:
    'No way to fit this player by dropping that one. Pick a different player.',
  infeasible_drop_not_active:
    'Player is not in an active slot. Drop-shuffle only works for active players.',
  infeasible_drop_not_owned: 'Player is not on your team.',
  infeasible_self_drop: 'Cannot drop the player you are trying to add.',
  infeasible_wrong_phase:
    'Drop-shuffle is available during regular season only.',
};

function humanizeInfeasible(reason) {
  return HUMAN_INFEASIBLE[reason] || 'Unable to generate plans for this drop.';
}

function playerLabel(p) {
  if (!p) return '';
  const fn = p?.info?.first_name || '';
  const ln = p?.info?.last_name || '';
  return `${fn} ${ln}`.trim() || p?.info?.name || 'Unknown';
}

export default function MobileRosterPlanModal({
  candidate,
  currentRoster,
  state,
  plans,
  selectedDropPlayer,
  selectedPlan,
  errorClass,
  errorMessage,
  infeasibleReason,
  onPickDrop,
  onChangeDrop,
  onSelectPlan,
  onConfirm,
  onCancel,
  onRetry,
  onDone,
  isVisible,
}) {
  // Surface lifecycle transitions to parent
  React.useEffect(() => {
    if (state === 'done' && onDone) onDone();
  }, [state, onDone]);

  // Backdrop tap dismissal — mobile-specific (mirrors
  // MobilePositionAssignmentModal.js:395)
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  // Hide for terminal/idle states
  if (
    !isVisible ||
    state === 'idle' ||
    state === 'cancelled' ||
    state === 'done'
  ) {
    return null;
  }

  const candidateName = playerLabel(candidate);
  const candidatePosition = candidate?.info?.position || '';

  return (
    <div
      className="fixed inset-0 bg-black/70"
      style={{ zIndex: 999999 }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative flex items-center justify-center min-h-screen"
        style={{ padding: '12px' }}
      >
        <div
          className={`${dynastyTheme.components.card.base} flex flex-col`}
          style={{
            width: '100%',
            maxWidth: '324px',
            maxHeight: '67.5vh',
            border: `1px solid ${dynastyTheme.tokens.colors.neutral[600]}`,
            boxShadow: `0 25px 50px -12px ${withAlpha(C.black, 0.25)}`,
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '13.5px' }}>
            {/* Header */}
            <div
              className="flex items-center justify-between"
              style={{ marginBottom: '13.5px' }}
            >
              <h2
                className={`font-bold ${dynastyTheme.classes.text.white} mb-0`}
                style={{
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <ArrowLeftRight
                  className={dynastyTheme.classes.text.primary}
                  style={{ width: '13px', height: '13px', flexShrink: 0 }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Make room for {candidateName}
                  {candidatePosition && ` (${candidatePosition})`}
                </span>
              </h2>
              <button
                onClick={onCancel}
                aria-label="Close"
                className={`${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.neutral} transition-colors rounded-lg`}
                style={{ padding: '3px' }}
              >
                <X style={{ width: '15px', height: '15px' }} />
              </button>
            </div>

            {/* Body */}
            {state === 'select_drop' && (
              <SelectDropPanel
                candidate={candidate}
                currentRoster={currentRoster}
                onPickDrop={onPickDrop}
              />
            )}

            {state === 'loading_plans' && (
              <LoadingPanel label="Finding plans..." />
            )}

            {state === 'choose_plan' && plans.length > 0 && (
              <ChoosePlanPanel
                candidate={candidate}
                selectedDropPlayer={selectedDropPlayer}
                plans={plans}
                selectedPlan={selectedPlan}
                onChangeDrop={onChangeDrop}
                onSelectPlan={onSelectPlan}
              />
            )}

            {state === 'choose_plan' && plans.length === 0 && (
              <InfeasiblePanel
                selectedDropPlayer={selectedDropPlayer}
                infeasibleReason={infeasibleReason}
                candidateName={candidateName}
                onChangeDrop={onChangeDrop}
              />
            )}

            {state === 'confirming' && (
              <LoadingPanel label="Executing plan..." />
            )}

            {state === 'error' && (
              <ErrorPanel
                errorClass={errorClass}
                errorMessage={errorMessage}
                onRetry={onRetry}
                onChangeDrop={onChangeDrop}
              />
            )}

            {/* Footer */}
            <div
              className="flex"
              style={{ gap: '9px', marginTop: '13.5px' }}
            >
              <button
                onClick={onCancel}
                className={`${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.darkLighter} transition-colors rounded-lg`}
                style={{ padding: '6px 12px', fontSize: '10.5px', flex: 1 }}
              >
                Cancel
              </button>

              {state === 'choose_plan' && plans.length > 0 && (
                <button
                  onClick={onConfirm}
                  disabled={!selectedPlan}
                  className={
                    selectedPlan
                      ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} hover:opacity-90 transition-colors rounded-lg`
                      : 'bg-neutral-700/50 text-neutral-500 cursor-not-allowed rounded-lg'
                  }
                  style={{
                    padding: '6px 12px',
                    fontSize: '10.5px',
                    flex: 1,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                >
                  <Check style={{ width: '12px', height: '12px' }} />
                  Confirm
                </button>
              )}

              {state === 'choose_plan' && plans.length === 0 && (
                <button
                  onClick={onChangeDrop}
                  className={`${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} hover:opacity-90 transition-colors rounded-lg`}
                  style={{
                    padding: '6px 12px',
                    fontSize: '10.5px',
                    flex: 1,
                    fontWeight: 600,
                  }}
                >
                  Different drop
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Subpanels
// ============================================================

function SelectDropPanel({ candidate, currentRoster, onPickDrop }) {
  const activeRoster = (currentRoster || []).filter(
    (p) => p?.league_context?.roster?.status === 'active'
  );

  // "Likely productive drops" first per § B.5.2 sort order
  const candidateEligibility = new Set(
    candidate?.league_context?.roster?.effective_eligibility || []
  );

  const sortedRoster = [...activeRoster].sort((a, b) => {
    const aSlot = a?.league_context?.roster?.position || '';
    const bSlot = b?.league_context?.roster?.position || '';
    const aType = aSlot.split('_')[0];
    const bType = bSlot.split('_')[0];

    const aProductive = candidateEligibility.has(aType);
    const bProductive = candidateEligibility.has(bType);
    if (aProductive && !bProductive) return -1;
    if (!aProductive && bProductive) return 1;

    const aPos = a?.info?.position || '';
    const bPos = b?.info?.position || '';
    if (aPos !== bPos) return aPos.localeCompare(bPos);

    return playerLabel(a).localeCompare(playerLabel(b));
  });

  if (sortedRoster.length === 0) {
    return (
      <div
        className={`${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight} rounded-lg`}
        style={{ padding: '12px', fontSize: '10.5px' }}
      >
        No active-slot players to drop.
      </div>
    );
  }

  return (
    <div>
      <p
        className={dynastyTheme.classes.text.neutralLight}
        style={{ fontSize: '10.5px', marginBottom: '9px' }}
      >
        Pick a player to drop:
      </p>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          maxHeight: '240px',
          overflowY: 'auto',
          paddingRight: '2px',
        }}
      >
        {sortedRoster.map((player) => {
          const slot = player?.league_context?.roster?.position;
          const salary = player?.league_context?.financial?.salary;
          const productive = candidateEligibility.has(
            (slot || '').split('_')[0]
          );
          return (
            <button
              key={player?.ids?.league_player || player?.ids?.mlb}
              onClick={() => onPickDrop(player?.ids?.mlb)}
              className={`${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5 rounded-lg border-2 transition-all duration-200 flex items-center justify-between text-left w-full`}
              style={{ padding: '9px' }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}
              >
                <span
                  className={`font-medium ${dynastyTheme.classes.text.white}`}
                  style={{
                    fontSize: '10.5px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {playerLabel(player)}
                </span>
                {productive && (
                  <span
                    className={`rounded ${dynastyTheme.classes.bg.success} ${dynastyTheme.classes.text.black} font-medium`}
                    style={{ fontSize: '8px', padding: '1.5px 5px', flexShrink: 0 }}
                  >
                    Direct
                  </span>
                )}
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
              >
                <span
                  className={dynastyTheme.classes.text.neutralLight}
                  style={{ fontSize: '9px' }}
                >
                  {slot}
                </span>
                {salary != null && (
                  <span
                    className={dynastyTheme.classes.text.neutralLight}
                    style={{ fontSize: '9px' }}
                  >
                    ${Math.round(salary)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChoosePlanPanel({
  candidate,
  selectedDropPlayer,
  plans,
  selectedPlan,
  onChangeDrop,
  onSelectPlan,
}) {
  const dropName = playerLabel(selectedDropPlayer);
  const dropSlot = selectedDropPlayer?.league_context?.roster?.position;
  const dropSalary = selectedDropPlayer?.league_context?.financial?.salary;
  const candidateName = playerLabel(candidate);

  return (
    <div>
      <div
        className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg flex items-center justify-between`}
        style={{ padding: '9px', marginBottom: '9px' }}
      >
        <span
          className={dynastyTheme.classes.text.white}
          style={{ fontSize: '10.5px', flex: 1, minWidth: 0 }}
        >
          Drop: <strong>{dropName}</strong>
          {dropSlot && (
            <span
              className={dynastyTheme.classes.text.neutralLight}
              style={{ fontSize: '9px', marginLeft: '6px' }}
            >
              {dropSlot}
            </span>
          )}
          {dropSalary != null && (
            <span
              className={dynastyTheme.classes.text.neutralLight}
              style={{ fontSize: '9px', marginLeft: '6px' }}
            >
              ${Math.round(dropSalary)}
            </span>
          )}
        </span>
        <button
          onClick={onChangeDrop}
          className={`${dynastyTheme.classes.text.primary} underline`}
          style={{ fontSize: '9px', flexShrink: 0 }}
        >
          change
        </button>
      </div>
      <p
        className={dynastyTheme.classes.text.neutralLight}
        style={{ fontSize: '10.5px', marginBottom: '6px' }}
      >
        Choose how to fit {candidateName}:
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {plans.map((plan) => {
          const isSelected =
            selectedPlan?.plan_fingerprint === plan.plan_fingerprint;
          const shapeLabel =
            plan.shape === 'direct_swap' ? 'Direct swap' : 'Drop with shuffle';
          return (
            <label
              key={plan.plan_fingerprint}
              className={`rounded-lg border-2 cursor-pointer transition-all duration-200 flex items-start ${
                isSelected
                  ? `${dynastyTheme.classes.border.primaryBright} bg-yellow-400/10`
                  : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5`
              }`}
              style={{ padding: '9px', gap: '9px' }}
            >
              <input
                type="radio"
                name="plan"
                checked={isSelected}
                onChange={() => onSelectPlan(plan)}
                style={{ marginTop: '2px', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className={`font-medium ${dynastyTheme.classes.text.white}`}
                  style={{ fontSize: '10.5px' }}
                >
                  {shapeLabel}
                </div>
                <div
                  className={dynastyTheme.classes.text.neutralLight}
                  style={{ fontSize: '9px', marginTop: '2px' }}
                >
                  {plan.description}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function InfeasiblePanel({
  selectedDropPlayer,
  infeasibleReason,
  candidateName,
  onChangeDrop,
}) {
  const reasonText = humanizeInfeasible(infeasibleReason);
  const dropName = playerLabel(selectedDropPlayer);
  return (
    <div>
      <div
        className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg flex items-center justify-between`}
        style={{ padding: '9px', marginBottom: '9px' }}
      >
        <span
          className={dynastyTheme.classes.text.white}
          style={{ fontSize: '10.5px' }}
        >
          Drop: <strong>{dropName}</strong>
        </span>
        <button
          onClick={onChangeDrop}
          className={`${dynastyTheme.classes.text.primary} underline`}
          style={{ fontSize: '9px' }}
        >
          change
        </button>
      </div>
      <div
        className={`${dynastyTheme.components.badge.warning} rounded-lg flex items-start`}
        style={{ padding: '9px', gap: '6px' }}
      >
        <AlertTriangle
          className={dynastyTheme.classes.text.warning}
          style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '1px' }}
        />
        <div
          className={dynastyTheme.classes.text.warning}
          style={{ fontSize: '10.5px' }}
        >
          {reasonText}
        </div>
      </div>
    </div>
  );
}

function LoadingPanel({ label }) {
  return (
    <div
      className="flex items-center justify-center"
      style={{ padding: '24px' }}
    >
      <Loader2
        className={`${dynastyTheme.classes.text.primary} animate-spin`}
        style={{ width: '18px', height: '18px' }}
      />
      <span
        className={dynastyTheme.classes.text.neutralLight}
        style={{ fontSize: '10.5px', marginLeft: '8px' }}
      >
        {label}
      </span>
    </div>
  );
}

function ErrorPanel({ errorClass, errorMessage, onRetry, onChangeDrop }) {
  const showRetry = errorClass === 'database_error';
  return (
    <div
      className={`${dynastyTheme.components.badge.error} rounded-lg flex items-start`}
      style={{ padding: '9px', gap: '6px' }}
    >
      <AlertTriangle
        className={dynastyTheme.classes.text.error}
        style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '1px' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className={`font-medium ${dynastyTheme.classes.text.error}`}
          style={{ fontSize: '10.5px' }}
        >
          Error
        </div>
        <div
          className={dynastyTheme.classes.text.error}
          style={{ fontSize: '9px', marginTop: '2px' }}
        >
          {errorMessage || 'Something went wrong'}
        </div>
        <div className="flex" style={{ gap: '12px', marginTop: '6px' }}>
          {showRetry && (
            <button
              onClick={onRetry}
              className={`${dynastyTheme.classes.text.primary} underline`}
              style={{ fontSize: '9px' }}
            >
              Retry
            </button>
          )}
          <button
            onClick={onChangeDrop}
            className={`${dynastyTheme.classes.text.primary} underline`}
            style={{ fontSize: '9px' }}
          >
            Different drop
          </button>
        </div>
      </div>
    </div>
  );
}
